import { useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Banner, TextArea } from '../../components/ui';
import type { ImportResult } from '../../types';

/**
 * Bringing in a catalogue.
 *
 * Paste from a spreadsheet rather than upload a file: an admin's
 * products live in Excel, selecting the rows and pressing Ctrl+C is
 * what they already know how to do, and it skips every question about
 * encodings, delimiters and which sheet.
 *
 * The preview is not a nicety. The failure mode of a bad import is 800
 * wrong rows and no undo, so nothing is written until the admin has seen
 * what would happen — including exactly which lines will be skipped and
 * why.
 */
export default function ProductImport({
  onClose, onDone,
}: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);

  const rows = parse(text);

  async function run(dryRun: boolean) {
    if (!rows.length) return;
    setBusy(true);
    try {
      const result = await api.post<ImportResult>('/orders/products/import', {
        rows, dryRun,
      });
      if (dryRun) {
        setPreview(result);
      } else {
        toast.success(
          `${result.created} added, ${result.updated} updated`
          + (result.failed.length ? `, ${result.failed.length} skipped` : '.'),
        );
        onDone();
      }
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not import that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Import products"
      subtitle="Paste from a spreadsheet. Existing codes are updated, new ones added."
      wide
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {preview ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || (preview.willCreate ?? 0) + (preview.willUpdate ?? 0) === 0}
              onClick={() => void run(false)}
            >
              Import {(preview.willCreate ?? 0) + (preview.willUpdate ?? 0)} products
            </button>
          ) : (
            <button type="button" className="btn btn-primary"
              disabled={busy || !rows.length} onClick={() => void run(true)}>
              Check {rows.length || ''} rows
            </button>
          )}
        </>
      )}
    >
      <div className="col gap-12">
        <Banner kind="info">
          <strong>Columns, in this order:</strong>{' '}
          <span className="mono small">
            code, name, category, brand, unit, hsn, gst%, mrp
          </span>
          <div style={{ marginTop: 4 }}>
            Only <strong>code</strong> and <strong>name</strong> are required. A header
            row is detected and skipped. Tabs (straight from Excel) or commas both work.
          </div>
        </Banner>

        <TextArea
          label="Paste your rows"
          rows={8}
          className="mono"
          placeholder={'SOAP-100\tLifeline Soap 100g\tPersonal care\tLifeline\tpiece\t3401\t18\t45'}
          value={text}
          onChange={(e) => { setText(e.target.value); setPreview(null); }}
        />

        {rows.length > 0 && !preview ? (
          <div className="small faint">
            {rows.length} row{rows.length === 1 ? '' : 's'} read. Nothing is saved until
            you have seen what it would do.
          </div>
        ) : null}

        {preview ? (
          <>
            <div className="stat-row">
              <div className="stat">
                <div className="stat-icon"><Icon name="plus" size={18} /></div>
                <div>
                  <div className="stat-label">New</div>
                  <div className="stat-value">{preview.willCreate}</div>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon"><Icon name="edit" size={18} /></div>
                <div>
                  <div className="stat-label">Updated</div>
                  <div className="stat-value">{preview.willUpdate}</div>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon"><Icon name="alert" size={18} /></div>
                <div>
                  <div className="stat-label">Skipped</div>
                  <div className={`stat-value${preview.failed.length ? ' danger' : ''}`}>
                    {preview.failed.length}
                  </div>
                </div>
              </div>
            </div>

            {preview.failed.length ? (
              <div className="card">
                <div className="card-head">
                  <h3>These rows will be skipped</h3>
                  <span className="small faint">The rest still import</span>
                </div>
                <div className="table-wrap" style={{ maxHeight: 220 }}>
                  <table className="data">
                    <thead>
                      <tr><th className="num">Row</th><th>Code</th><th>Why</th></tr>
                    </thead>
                    <tbody>
                      {preview.failed.map((f) => (
                        <tr key={`${f.line}-${f.code}`}>
                          <td className="num">{f.line}</td>
                          <td className="mono small">{f.code || <span className="faint">—</span>}</td>
                          <td>{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <Banner kind="warning">
              Importing sets names, units and GST rates from this file.{' '}
              <strong>It does not set prices</strong> — those live on a price list, and a
              product with no price cannot be ordered.
            </Banner>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Excel copies as tab-separated; a CSV export is comma-separated. Both
 * arrive through the same paste box, so the delimiter is worked out from
 * the content rather than asked about.
 */
function parse(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];

  const delimiter = (lines[0] ?? '').includes('\t') ? '\t' : ',';
  const split = (line: string) => line.split(delimiter).map((cell) => cell.trim());

  // A first row whose second column reads like a header is a header.
  const first = split(lines[0] ?? '');
  const hasHeader = /^(code|sku|item.?code)$/i.test(first[0] ?? '')
    || /^(name|product|description)$/i.test(first[1] ?? '');

  return lines.slice(hasHeader ? 1 : 0).map((line) => {
    const [code, name, category, brand, unit, hsnCode, gstRate, mrp] = split(line);
    return {
      code: code ?? '',
      name: name ?? '',
      category: category || undefined,
      brand: brand || undefined,
      unit: unit || undefined,
      hsnCode: hsnCode || undefined,
      gstRate: gstRate || undefined,
      mrp: mrp || undefined,
    } as Record<string, string>;
  });
}
